import { twApi } from './../api';
import { PaginateResponse } from './../../Global.model';
import {
  AddRateTableDocumentRequestDto,
  AddRateTableRequestDto,
  AddRateTableResponseDto,
  SearchRateTablesParams,
  SearchRateTablesResponseDto,
  UpdateRateTableRequestDto
} from './rateTable.model';

const rateTableApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getRateTableById: builder.query<SearchRateTablesResponseDto, string>({
      query: (rateTableId) => ({
        url: `/rate-tables/${rateTableId}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: SearchRateTablesResponseDto }) => ({
        ...response.data
      }),
      providesTags: (result) => (result ? [{ type: 'RateTables', id: result.id }] : [])
    }),
    searchRateTables: builder.query<
      PaginateResponse<SearchRateTablesResponseDto>,
      SearchRateTablesParams
    >({
      query: (arg) => ({
        url: '/rate-tables/search',
        method: 'POST',
        params: { ...arg.params },
        body: { ...arg.filters }
      }),
      transformResponse: (response: {
        data: SearchRateTablesResponseDto[];
        more_records: boolean;
        size: number;
        total: number;
      }) => ({
        content: response.data,
        more_records: response.more_records,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'RateTables' as const, id })),
              { type: 'RateTables', id: 'RATE_TABLES_LIST' },
              { type: 'RateTableCount', id: 'TOTAL_RATE_TABLES' }
            ]
          : [
              { type: 'RateTables', id: 'RATE_TABLES_LIST' },
              { type: 'RateTableCount', id: 'TOTAL_RATE_TABLES' }
            ]
    }),
    getTotalRateTables: builder.query<
      {
        success: boolean;
        data: number;
      },
      string
    >({
      query: (customerId) => ({
        url: `/rate-tables/customer/${customerId}/count`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'RateTables', id: 'RATE_TABLES_LIST' },
              { type: 'RateTableCount', id: 'TOTAL_RATE_TABLES' }
            ]
          : [{ type: 'RateTables', id: 'TOTAL_RATE_TABLES' }]
    }),
    addRateTable: builder.mutation<AddRateTableResponseDto, AddRateTableRequestDto>({
      query: (body) => ({
        url: '/rate-tables',
        method: 'POST',
        body
      }),
      transformResponse: (response: AddRateTableResponseDto) => ({
        ...response
      }),
      invalidatesTags: [
        { type: 'RateTables', id: 'RATE_TABLES_LIST' },
        { type: 'RateTableCount', id: 'TOTAL_RATE_TABLES' }
      ]
    }),
    addRateTableDocument: builder.mutation<AddRateTableResponseDto, AddRateTableDocumentRequestDto>(
      {
        query: (body) => {
          const formData = new FormData();

          formData.append('document', body.file);

          return {
            url: `/rate-tables/${body.rateTableId}/document`,
            method: 'POST',
            body: formData
          };
        },
        transformResponse: (response: AddRateTableResponseDto) => ({
          data: response.data
        }),
        invalidatesTags: (response, error, data) => [{ type: 'RateTables', id: data.rateTableId }]
      }
    ),
    deleteRateTable: builder.mutation<void, string>({
      query: (rateTableId) => ({
        url: `/rate-tables/${rateTableId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: 'RateTables', id: 'RATE_TABLES_LIST' },
        { type: 'RateTableCount', id: 'TOTAL_RATE_TABLES' }
      ]
    }),
    updateRateTable: builder.mutation<AddRateTableResponseDto, UpdateRateTableRequestDto>({
      query: (body) => ({
        url: `/rate-tables/${body.id}`,
        method: 'PUT',
        body
      }),
      transformResponse: (response: { data: AddRateTableResponseDto }) => ({
        ...response.data
      }),
      invalidatesTags: (response, error, data) => [
        { type: 'RateTables', id: data.id },
        { type: 'RateTables', id: 'RATE_TABLES_LIST' }
      ]
    })
  })
});

export const {
  useGetRateTableByIdQuery,
  useSearchRateTablesQuery,
  useGetTotalRateTablesQuery,
  useAddRateTableMutation,
  useAddRateTableDocumentMutation,
  useDeleteRateTableMutation,
  useUpdateRateTableMutation
} = rateTableApi;
